'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useId, useState } from 'react';
import type { JobLocationsInCityResolved } from './types';
import { Icon, IconType } from '@/components/Icon';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { Text } from '@/lib/render/Text';

// The city's Standorte as recruiting entries (10.03.03.27 Job Locations In
// City): same card grid as List Of Locations In City, minus its icons and
// contact row — each card carries only its title, address and one link to the
// application form further down the same page. No widget title: editors place
// a Title Widget before it.
//
// Beyond `COLLAPSED_COUNT` entries the rest start hidden behind a toggle, as on
// the legacy page. They stay in the HTML, so crawlers still see every Standort.
const COLLAPSED_COUNT = 4;

export function JobLocationsInCityComponent({
  resolved,
}: {
  resolved: JobLocationsInCityResolved;
}) {
  const t = useTranslations('jobLocationsInCity');
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const items = resolved.items ?? [];

  if (items.length === 0) {
    return null;
  }

  const applyLabel = resolved.applyLabel || t('applyLabel');
  const tracking = resolved.tracking?.length ? resolved.tracking : undefined;
  const collapsible = items.length > COLLAPSED_COUNT;

  return (
    <section className="pt-4">
      <ul
        id={listId}
        className="grid list-none grid-cols-1 gap-4 md:grid-cols-2"
      >
        {items.map((item, index) => (
          <li
            key={item.slug}
            className={classNames(
              'bg-secondary-lighter my-0 flex flex-col items-center gap-3 rounded-2xl px-4 py-8 text-center',
              !expanded && index >= COLLAPSED_COUNT && 'hidden',
            )}
          >
            <div className="flex flex-col items-center gap-1">
              <p className="h3 my-0 text-base font-medium">
                {t('itemTitle', { name: item.name })}
              </p>
              {item.address && (
                <p className="my-0">
                  {[
                    item.address.street,
                    [item.address.postcode, item.address.city]
                      .filter(Boolean)
                      .join(' '),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>
            <a
              href={item.href}
              className="text-primary font-medium hover:underline"
              onClick={
                tracking
                  ? () =>
                      pushCmsEventTracking(tracking, {
                        label: t('itemTitle', { name: item.name }),
                      })
                  : undefined
              }
            >
              <Text field="applyLabel">{applyLabel}</Text>
            </a>
          </li>
        ))}
      </ul>
      {collapsible && (
        <div className="flex justify-center pt-4">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={listId}
            onClick={() => setExpanded((value) => !value)}
            className="text-primary inline-flex items-center gap-1 font-medium hover:underline"
          >
            {expanded ? t('showLess') : t('showMore')}
            <Icon
              src={IconType.SvgChevronDown}
              size={16}
              className={classNames(expanded && 'rotate-180')}
            />
          </button>
        </div>
      )}
    </section>
  );
}
